import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';

import { Input } from '../../../../components/ui/input.tsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.tsx';
import useParent from '../../../../hooks/useParent.js';

const formSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  gender: z.enum(['Male', 'Female'], {
    errorMap: () => ({ message: 'Gender must be either "Male" or "Female"' }),
  }),
  country: z.string().min(1, { message: 'Country is required' }),
  phoneNumber: z
    .string()
    .min(10, { message: 'Phone number must be at least 10 digits' }),
  address: z.string().min(1, { message: 'Address is required' }),
  avatar: z.any().refine((file) => file instanceof File || file === undefined, {
    message: 'Invalid file',
  }),
});

const AccountInformation = () => {
  const { state } = useLocation();
  const { parent, updateParent, updateParentPending } = useParent();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: parent.firstName,
      lastName: parent.lastName,
      gender: parent.gender || 'Male',
      country: 'Nigeria',
      phoneNumber: parent.phoneNumber,
      address: parent.address,
    },
  });

  const onSubmit = async (values) => {
    const formData = new FormData();

    formData.append('firstName', values.firstName);
    formData.append('lastName', values.lastName);
    formData.append('gender', values.gender);
    formData.append('country', values.country);
    formData.append('phoneNumber', values.phoneNumber);
    formData.append('address', values.address);

    if (values.avatar instanceof File) {
      formData.append('avatar', values.avatar);
    }

    updateParent([parent.parentId, formData]);
  };

  const fileInputRef = useRef(null);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.5,
        ease: 'easeInOut',
      }}
    >
      <p className="text-xl mb-4">{state.label}</p>
      <div className="p-10 bg-white">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            encType="multipart/form-data"
          >
            {form.formState.errors.root && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
                {form.formState.errors.root.message}
              </div>
            )}
            <div className="mb-7 md:flex gap-x-5 items-center">
              <div className="md:w-24 md:h-24 h-12 w-12 mb-3 flex items-center justify-center bg-purple-100 text-purple-700 rounded-full shadow-[0_0_20px_5px_rgba(128,90,213,0.3)] outline-white outline-1 outline overflow-hidden">
                {parent.photoUrl ? (
                  <img
                    src={parent.photoUrl}
                    alt="Parent"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <p className="md:text-4xl text-2xl">
                    {parent.firstName[0]}
                    {parent.lastName[0]}
                  </p>
                )}
              </div>
              <FormField
                control={form.control}
                name="avatar"
                render={({ field }) => (
                  <>
                    <Input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file);
                      }}
                      className="hidden"
                    />
                    <button
                      type="button"
                      className=" h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      onClick={handleFileButtonClick}
                    >
                      Change Avatar
                    </button>
                    <p className="mt-2 text-sm text-gray-500">
                      {form.watch('avatar')?.name ?? 'No file selected'}
                    </p>
                  </>
                )}
              />
            </div>
            <div className="md:flex gap-x-10 mb-7">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      FirstName
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Feyikemi"
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      LastName
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Adeniji"
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="md:flex gap-x-10 mb-7">
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      Gender
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        name={field.name}
                      >
                        <SelectTrigger
                          id="gender"
                          className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      Country
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Nigeria"
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                        value={'Nigeria'}
                        disabled={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="md:flex gap-x-10 mb-7">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+234 803 123 4567"
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-[#4d4d4d] text-xs md:text-sm">
                      Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="No 24, old Police Post Molete Ibadan"
                        {...field}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none h-7 bg-gray-100 py-6"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-10">
              <button
                type="submit"
                className=" h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                disabled={updateParentPending}
              >
                {updateParentPending ? 'Loading' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </motion.div>
  );
};

export default AccountInformation;
